import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { queryOptions } from "@tanstack/react-query";

export type ProfileData = {
  tenant_user_id: string | null;
  firstname: string | null;
  lastname: string | null;
  position?: string | null;
};

async function getTenantUserId(authUserId: string): Promise<string | null> {
  const { data: tenantUser, error } = await analysisClient
    .from("tenant_user")
    .select("tenant_user_id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();

  if (error) throw error;
  return tenantUser?.tenant_user_id || null;
}

async function getUserSettings(tenantUserId: string): Promise<{
  firstname: string | null;
  lastname: string | null;
  position: string | null;
}> {
  const { data: settingData, error } = await analysisClient
    .from("tenant_user_setting")
    .select("settings")
    .eq("tenant_user_id", tenantUserId)
    .maybeSingle();

  if (error) throw error;

  if (settingData?.settings && typeof settingData.settings === "object") {
    const settings = settingData.settings as Record<string, unknown>;
    return {
      firstname: (settings.firstname as string) || null,
      lastname: (settings.lastname as string) || null,
      position: (settings.position as string) || null,
    };
  }

  return {
    firstname: null,
    lastname: null,
    position: null,
  };
}

async function fetchUserProfile(
  authUserId: string,
): Promise<ProfileData | null> {
  const tenantUserId = await getTenantUserId(authUserId);
  const settings = tenantUserId
    ? await getUserSettings(tenantUserId)
    : {
        firstname: null,
        lastname: null,
        position: null,
      };

  return {
    tenant_user_id: tenantUserId,
    firstname: settings.firstname,
    lastname: settings.lastname,
    position: settings.position,
  };
}

export const profileQueries = {
  all: getKeyOnlyQueryConfig(["profile"]),

  current: (authUserId: string | undefined) =>
    queryOptions({
      queryKey: [...profileQueries.all.queryKey, "current", authUserId],
      queryFn: () => {
        if (!authUserId) throw new Error("Auth user ID is required");
        return fetchUserProfile(authUserId);
      },
      enabled: Boolean(authUserId),
    }),
};
